import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'boss_auth_token';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  // Detect whether we're in a popup (has an opener from our origin)
  const isPopup = (() => {
    try {
      return !!(window.opener && window.opener !== window && window.opener.location.origin === window.location.origin);
    } catch {
      return !!window.opener;  // cross-origin opener still counts as popup
    }
  })();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errorParam = params.get('error');

    const reportError = (reason) => {
      if (isPopup) {
        try { window.opener.postMessage({ type: 'google-auth-error', reason }, window.location.origin); } catch {}
        window.close();
      } else {
        navigate('/login?error=google_auth_failed', { replace: true });
      }
    };

    if (errorParam) return reportError(errorParam);
    // State is stored in the PARENT's sessionStorage (popup has its own scope)
    let expectedState = null;
    try {
      if (isPopup && window.opener && !window.opener.closed) {
        expectedState = window.opener.sessionStorage.getItem('google_oauth_state');
        window.opener.sessionStorage.removeItem('google_oauth_state');
      } else {
        expectedState = sessionStorage.getItem('google_oauth_state');
        sessionStorage.removeItem('google_oauth_state');
      }
    } catch {
      // Cross-origin access blocked — fall back to own storage
      expectedState = sessionStorage.getItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_state');
    }
    if (!code || !state || state !== expectedState) return reportError('state_mismatch');

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google';

    (async () => {
      try {
        const res = await fetch(`${API}/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, redirect_uri: redirectUri }),
        });
        if (!res.ok) return reportError('exchange_failed');
        const data = await res.json();

        if (isPopup) {
          // Send token + user back to the parent window and close
          window.opener.postMessage(
            { type: 'google-auth-success', token: data.token, user: data },
            window.location.origin
          );
          window.close();
          // If window.close() failed (rare), show a friendly instruction
          setTimeout(() => setError('signed_in_please_close'), 500);
        } else {
          // Full-page-redirect fallback path
          if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
          await refreshUser();
          navigate('/account', { replace: true });
        }
      } catch {
        reportError('exchange_failed');
      }
    })();
  }, [navigate, refreshUser, isPopup]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand px-4" data-testid="google-auth-callback">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-bush mx-auto" />
        <p className="text-sm text-gray-500 mt-4">
          {error === 'signed_in_please_close' ? 'Signed in — you can close this window.' : 'Signing you in…'}
        </p>
      </div>
    </div>
  );
}
